<template>
	<form class="ideas-app-search-form" @submit.prevent="submit">
		<div class="input">
			<v-icon slug="magnifying-glass" />

			<input v-model="q" type="search" name="search" placeholder="Your Dream Idea" />
		</div>

		<div class="rate">
			<p>$ per hour</p>

			<div ref="rate_slider" />

			<fieldset>
				<label class="checkbox">
					<input v-model="rate_varies" type="checkbox" @change="on_varies_change" />
					<v-icon slug="check" />
					<span>Varies</span>
				</label>
			</fieldset>
		</div>

		<button type="submit" class="btn btn-solid">Search</button>
	</form>

	<div class="ideas-app-inner">
		<aside :class="'ideas-app-filters ' + (filters_open ? 'open' : '')">
			<header>
				<h4>Start Here</h4>

				<p>Select ALL checkboxes that apply to you.</p>

				<button type="button" class="btn btn-bordered" @click="toggle_filters">Filter</button>
			</header>

			<div class="ideas-app-filters-inner">
				<div v-if="selected_items.length > 0" class="ideas-app-selected-filters">
					<button
						v-for="item in selected_items"
						:key="item.key + '-' + item.value"
						class="btn btn-solid light-orange small"
						type="button"
						@click="remove(item.key, item.value)"
					>
						<span class="label">{{ item.label }}</span>

						<span class="icon-wrapper"><v-icon slug="xmark" /></span>
					</button>
				</div>

				<v-filter
					v-for="(filter, key) in store.filters"
					:key="key"
					:slug="key"
					:filter="filter"
					:selected_filters="selected_filters"
					@update="submit"
				/>

				<button type="button" class="btn btn-link small" @click="clear">Clear All</button>
			</div>
		</aside>

		<div v-if="fetching && !fetched" class="spinner">
			<div class="bounce1" />
			<div class="bounce2" />
			<div class="bounce3" />
		</div>

		<template v-else-if="fetched">
			<div v-if="results.length > 0" ref="results_el" class="ideas-app-results">
				<v-result
					v-for="result in results"
					:key="result.id"
					:item="result"
					:selected_filters="selected_filters"
				/>
			</div>

			<div v-else class="ideas-app-no-results">
				<p>No results found.</p>
			</div>
		</template>
	</div>
</template>

<script setup>
import { useEventListener, useUrlSearchParams } from '@vueuse/core';
import noUiSlider from 'nouislider/dist/nouislider.js';
import { computed, onMounted, reactive, ref } from 'vue';

import { rest_request } from '../_partials/_rest.js';
import { sync_query_params } from '../_partials/_utilities.js';
import { store } from '../jobs-app/_store.js';
import VIcon from '../jobs-app/_v-icon.vue';
import VFilter from './_v-filter.vue';
import VResult from './_v-result.vue';

const params = useUrlSearchParams(`history`, {
	writeMode: `push`,
});

function get_value(key) {
	let items = [];

	switch (typeof params[key]) {
		case `object`:
			items = params[key];
			break;
		case `string`:
			items.push(params[key]);
			break;
	}

	items = items.filter((item) => item !== ``);

	return items;
}

const results = ref([]);

const q = ref(typeof params.q === `string` ? params.q : ``);

let selected_filters = {};

for (const key of store.filter_keys) {
	selected_filters[key] = get_value(key);
}

selected_filters = reactive(selected_filters);

const min = 0;

const max = 200;

const rate_varies = ref(false);
rate_varies.value = params.rate_varies === `true`;

const rate_min = ref(null);
rate_min.value = params.rate_min ? parseInt(params.rate_min, 10) : min;

const rate_max = ref(null);
rate_max.value = params.rate_max ? parseInt(params.rate_max, 10) : max;

const selected_items = computed(() => {
	const items = [];

	for (const key of store.filter_keys) {
		for (const term of store.filters[key].terms) {
			if (selected_filters[key].indexOf(term.value) === -1) {
				continue;
			}

			items.push({
				key,
				value: term.value,
				label: term.label,
			});
		}
	}

	return items;
});

const fetching = ref(false);

const fetched = ref(false);

const filters_open = ref(false);

let controller = null;

const page = ref(1);

const has_more = ref(false);

const rate_slider = ref(null);

const ticking = ref(false);

const last_scroll_y = ref(0);

const results_el = ref(null);

function load() {
	if (controller) {
		controller.abort();
	}

	fetching.value = true;

	controller = new AbortController();

	const data = {
		page: page.value,
		q: q.value,
	};

	for (const key of store.filter_keys) {
		data[key] = selected_filters[key];
	}

	if (!rate_varies.value) {
		data.rate_min = rate_min.value;
		data.rate_max = rate_max.value;
	}

	rest_request(`ideas/search`, {
		method: `POST`,
		signal: controller.signal,
		data,
	})
		.then((response) => {
			for (let i = 0; i < response.length; i++) {
				results.value.push(response[i]);
			}

			has_more.value = response.length >= store.per_page;
			fetched.value = true;
		})
		.catch(() => {
			has_more.value = false;
		})
		.then(() => {
			fetching.value = false;
		});
}

function submit() {
	const query = {};

	if (q.value && q.value !== ``) {
		query.q = q.value;
	}

	for (const key of store.filter_keys) {
		if (selected_filters[key].length > 0) {
			query[key] = selected_filters[key];
		}
	}

	query.rate_varies = rate_varies.value;
	query.rate_min = rate_min.value;
	query.rate_max = rate_max.value;

	sync_query_params(params, query);

	fetched.value = false;

	page.value = 1;

	results.value = [];

	load();
}

function remove(key, value) {
	const idx = selected_filters[key].indexOf(value);
	if (idx > -1) {
		selected_filters[key].splice(idx, 1);
	}

	submit();
}

function clear() {
	for (const key of store.filter_keys) {
		selected_filters[key].splice(0, selected_filters[key].length);
	}

	submit();
}

function toggle_filters() {
	filters_open.value = !filters_open.value;
}

function more() {
	if (fetching.value) {
		return;
	}

	page.value++;
	load();
}

function on_update(values, handle, unencoded) {
	rate_min.value = Math.round(unencoded[0]);
	rate_max.value = Math.round(unencoded[1]);
	submit();
}

function on_varies_change() {
	if (rate_varies.value) {
		rate_slider.value.noUiSlider.disable();
	} else {
		rate_slider.value.noUiSlider.enable();
	}
}

function scroll() {
	last_scroll_y.value = window.pageYOffset;
	ticking.value = false;

	if (!results_el.value) {
		return;
	}

	if (!has_more.value) {
		return;
	}

	let percent = last_scroll_y.value;

	percent -= results_el.value.offsetTop;

	percent /= results_el.value.clientHeight;

	if (percent > 0.9) {
		more();
	}
}

function request_tick() {
	if (ticking.value) {
		return;
	}

	window.requestAnimationFrame(scroll);
	ticking.value = true;
}

onMounted(() => {
	const slider = noUiSlider.create(rate_slider.value, {
		range: {
			min,
			max,
		},
		step: 5,
		start: [rate_min.value, rate_max.value],
		tooltips: true,
		format: {
			to(value) {
				return value;
			},
			from(value) {
				return Number(value);
			},
		},
		connect: true,
	});

	slider.on(`set`, on_update);

	on_varies_change();

	submit();
});

useEventListener(window, `scroll`, request_tick, {
	passive: true,
});
</script>
